"use client";

import { bodyWithoutLeadingTitle } from "@/app/(public)/help/_components/help-article-view";
import { HelpMarkdown } from "@/app/(public)/help/_components/help-markdown";
import { helpSpaHref } from "@/app/(public)/help/_components/help-spa-path";
import { api } from "@v1/backend/convex/_generated/api";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { useQuery } from "convex/react";
import { ChevronRight, FileText, SquareArrowOutUpRight } from "lucide-react";
import { useTranslations } from "next-intl";
import type { MouseEvent, ReactElement } from "react";
import { useMessenger } from "./messenger-context";
import { MESSENGER_ICON_BUTTON_CLASS, MessengerDetailHeader } from "./messenger-header";
import { helpPageIdOf } from "./messenger-sources";

/** One help article inside the wide window; help links stay in the window. */
export function MessengerArticleView({ pageId }: { readonly pageId: string }): ReactElement {
  const t = useTranslations("messenger");
  const { config, nav } = useMessenger();
  const result = useQuery(api.pages.public_help.getPublicHelpPage, {
    org_slug: config.help_org_slug,
    page_id: pageId,
  });
  const page = result?.state === "ok" ? result : null;

  const keepLinksInside = (event: MouseEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest("a");
    const href = anchor?.getAttribute("href");
    if (!href) return;
    const target = helpPageIdOf(href, config.help_org_slug);
    if (target === null) return;
    event.preventDefault();
    nav.push({ kind: "article", pageId: target });
  };

  return (
    <>
      <MessengerDetailHeader
        title={page?.space.name ?? ""}
        actions={
          page ? (
            <a
              href={helpSpaHref(config.help_org_slug, { kind: "page", pageId })}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("header.neuerTab")}
              className={MESSENGER_ICON_BUTTON_CLASS}
            >
              <SquareArrowOutUpRight className="h-4 w-4" strokeWidth={2} aria-hidden />
            </a>
          ) : null
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" onClickCapture={keepLinksInside}>
        {page === null ? null : (
          <article className="mx-auto w-full max-w-[640px] px-6 pb-10 pt-6 sm:px-8">
            <h1 className="text-[22px] font-bold leading-tight tracking-tight text-primary">
              {page.page.icon ? (
                <span className="mr-2 inline-flex align-middle">
                  <IconOrEmoji value={page.page.icon} fallback="" size={20} />
                </span>
              ) : null}
              {page.page.title}
            </h1>
            <div className="mt-5 text-[14px]">
              <HelpMarkdown
                content_md={bodyWithoutLeadingTitle(page.content_md, page.page.title)}
                org_slug={page.org_slug}
                link_targets={page.link_targets}
              />
            </div>
            {page.children.length > 0 ? (
              <ul className="mt-8 divide-y divide-border/40 border-y border-border/40">
                {page.children.map((child) => (
                  <li key={child.page_id}>
                    <button
                      type="button"
                      onClick={() => nav.push({ kind: "article", pageId: child.page_id })}
                      className="flex min-h-11 w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-action-500/[0.03]"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-primary/30" strokeWidth={1.75} aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-[14px] text-primary/85">{child.title}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-primary/30" strokeWidth={2} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        )}
      </div>
    </>
  );
}
